import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { isEmpty, startCase } from "lodash";
import { useContextSelector } from "use-context-selector";
import { useTranslation } from "react-i18next";
import Layout from "../Layout";
import AuthContext from "../../context/AuthContext";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { betCheckObj, toFixedWithoutRounding } from "../../Utils/Toast";
import { FaArrowRight } from "react-icons/fa";
// Maps the label used by the game-type filter to the betType key the
// profitLoss API expects — unchanged from BettingSettled.jsx
const gameCategories = {
  Casino: "casino",
  BetFair: "betfair",
  Bookmaker: "bookmaker",
  SportsBook: "sportBook",
  Fancy: "fancy",
};

// Same quick date-range presets as the old BettingHistoryFilter.jsx
const timeRanges = [
  {
    name: "Today",
    labelKey: "Today",
    getDays: () => ({
      fromPeriod: moment().format("YYYY-MM-DD"),
      toPeriod: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    name: "Yesterday",
    labelKey: "Yesterday",
    getDays: () => ({
      fromPeriod: moment().subtract(1, "days").format("YYYY-MM-DD"),
      toPeriod: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    name: "Last 7 days",
    labelKey: "Last7Days",
    getDays: () => ({
      fromPeriod: moment().subtract(6, "days").format("YYYY-MM-DD"),
      toPeriod: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    name: "Last 30 days",
    labelKey: "Last30Days",
    getDays: () => ({
      fromPeriod: moment().subtract(29, "days").format("YYYY-MM-DD"),
      toPeriod: moment().format("YYYY-MM-DD"),
    }),
  },
];

const BettingHistory = () => {
  const { t } = useTranslation();
  const previousDate = moment().subtract(6, "days").format("YYYY-MM-DD");
  const currentDate = moment().format("YYYY-MM-DD");

  const profileData = useContextSelector(AuthContext, (ctx) => {
    return ctx.profileData;
  });

  // -- filter panel state (no design was given for this, built inline
  // using the same card/input building blocks as the rest of the new HTML) --
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState({
    platform: "",
    gameType: "Casino",
    fromPeriod: previousDate,
    toPeriod: currentDate,
  });

  // -- BettingSettled.jsx state/logic (settled only, unsettled dropped) --
  const [data, setData] = useState([]);
  const [single, setSingle] = useState({});
  const [active, setActive] = useState([]); // expanded bet rows in detail view
  const [settledFilter, setSettledFilter] = useState(() => ({
    betType: gameCategories[filter?.gameType] ?? "betfair",
    status: "completed",
    fromPeriod: filter?.fromPeriod,
    toPeriod: filter?.toPeriod,
    page: 1,
    limit: 50,
  }));

  const groupByUpdatedDateWithBets = (arr) => {
    const filtered = arr.filter((item) => item?.bets_list?.length > 0);
    const sorted = filtered.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );
    const grouped = sorted.reduce((acc, item) => {
      const date = new Date(item.updatedAt).toISOString().split("T")[0];
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).map(([date, markets]) => ({
      date,
      markets,
    }));
  };

  const calculateTotal = (markets, newTab) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;
    const commissionRate = parseFloat(profileData?.commission) || 0;

    return markets.reduce((marketAcc, market) => {
      let backProfit = 0,
        backLoss = 0,
        layProfit = 0,
        layLoss = 0,
        yesProfit = 0,
        yesLoss = 0,
        noProfit = 0,
        noLoss = 0;

      market.bets_list = market?.bets_list?.map((bet) => {
        const profit = parseFloat(bet.profitAmount) || 0;
        const loss = parseFloat(bet.loseAmount) || 0;
        let updateResultValue = 0;

        if (bet?.betType === "back") {
          if (bet?.teamSelectionWin === bet?.selectionId) {
            backProfit += profit;
            updateResultValue = profit;
          } else {
            backLoss += loss;
            updateResultValue = -loss;
          }
        } else if (bet?.betType === "lay") {
          if (bet?.teamSelectionWin !== bet?.selectionId) {
            layProfit += profit;
            updateResultValue = profit;
          } else {
            layLoss += loss;
            updateResultValue = -loss;
          }
        }

        if (bet?.type === "Yes") {
          if (bet?.decisionRun >= bet?.betRun) {
            yesProfit += profit;
            updateResultValue = profit;
          } else {
            yesLoss += loss;
            updateResultValue = -loss;
          }
        } else if (bet?.type === "No") {
          if (bet?.decisionRun < bet?.betRun) {
            noProfit += profit;
            updateResultValue = profit;
          } else {
            noLoss += loss;
            updateResultValue = -loss;
          }
        }

        return { ...bet, updatedResult: updateResultValue };
      });

      const backSubTotal = backProfit - backLoss;
      const laySubTotal = layProfit - layLoss;
      const marketSubTotal = backSubTotal + laySubTotal;
      const yesSubTotal = yesProfit - yesLoss;
      const noSubTotal = noProfit - noLoss;
      const fancySubTotal = yesSubTotal + noSubTotal;

      let netAmount = 0;
      if (newTab === "toss" || newTab === "tie") {
        netAmount = marketSubTotal;
      } else if (newTab === "fancy") {
        netAmount = fancySubTotal;
      } else {
        const commission =
          marketSubTotal > 0 ? (marketSubTotal * commissionRate) / 100 : 0;
        netAmount = marketSubTotal - commission;
      }

      return marketAcc + netAmount;
    }, 0);
  };

  const TotalLength = (markets) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;
    return markets.reduce((total, match) => total + match.bets_list.length, 0);
  };

  const calculateTotalAmount = (markets) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;
    return markets.reduce((total, match) => {
      const matchTotal = match.bets_list.reduce(
        (sum, bet) => sum + Number(bet.amount || 0),
        0,
      );
      return total + matchTotal;
    }, 0);
  };

  const getData = async () => {
    const { status, data } = await apiGet(apiPath.profitLoss, settledFilter);
    if (status == 200) {
      if (data?.success) {
        setData(
          data?.results?.data?.length > 0
            ? settledFilter?.betType != "casino"
              ? groupByUpdatedDateWithBets(data?.results?.data)?.map((res) => ({
                  ...res,
                  total: calculateTotal(res?.markets, settledFilter?.betType),
                  totalBetAmount: calculateTotalAmount(res?.markets),
                }))
              : data?.results?.data
            : [],
        );
      }
    }
  };

  const pendingFetch = useRef(false);
  const isFirstRender = useRef(true);

  // filter (date range / game type / confirm) -> settledFilter
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const newBetType = filter?.gameType
      ? (gameCategories[filter.gameType] ?? "betfair")
      : "betfair";

    setSettledFilter((prev) => ({
      ...prev,
      betType: newBetType,
      fromPeriod: filter?.fromPeriod || prev.fromPeriod,
      toPeriod: filter?.toPeriod || prev.toPeriod,
    }));

    pendingFetch.current = true;
  }, [filter]);

  useEffect(() => {
    if (!show && pendingFetch.current) {
      pendingFetch.current = false;
      getData();
    }
  }, [show]);

  useEffect(() => {
    getData(); // initial fetch only
  }, []);

  // -- detail view (BettingSettledDetail, ported into this file using the
  // same mobile-reward-table row pattern — no design existed for the old
  // accordion layout, so I rebuilt it flat instead of nesting) --
  const total = toFixedWithoutRounding(Math.abs(single?.total));
  const detailList =
    settledFilter?.betType == "casino"
      ? single
      : single?.markets
          ?.map((res) => {
            return res?.bets_list?.map((item) => ({
              ...item,
              market:
                settledFilter?.betType == "fancy" ? t("FancyBet") : res?.market,
            }));
          })
          .flat();

  return (
    <Layout>
      <div>
        <div className="history-overview-wrapper">
          <div className="desktop_card v-card v-sheet theme--light elevation-0">
            <div className="history-card v-card v-sheet theme--light elevation-0">
              {isEmpty(single) && (
                <div className="row no-gutters">
                  <div className="col col-12">
                    <div className="row">
                      <div className="pt-4 pb-2 px-4 date-padding col col-12">
                        <div
                          onClick={() => setShow((prev) => !prev)}
                          className="v-input date-search-mobile mb-3 elevation-0  text-center v-input--hide-details v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined"
                        >
                          <div className="v-input__control">
                            <div className="v-input__slot">
                              <fieldset aria-hidden="true">
                                <legend style={{ width: "0px" }}>
                                  <span className="notranslate">​</span>
                                </legend>
                              </fieldset>
                              <div className="v-text-field__slot">
                                <input
                                  id="input-1134"
                                  type="text"
                                  readOnly
                                  value={`${moment(filter?.fromPeriod).format("YYYY-MM-DD")} - ${moment(
                                    filter?.toPeriod,
                                  ).format(
                                    "YYYY-MM-DD",
                                  )} | ${filter?.gameType}`}
                                />
                              </div>
                              <div className="v-input__append-inner">
                                <div className="v-input__icon v-input__icon--append">
                                  <i
                                    aria-hidden="true"
                                    className="v-icon notranslate material-icons theme--light"
                                  >
                                    {show ? "close" : "search"}
                                  </i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="v-menu">{/**/}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filter — old logic (game type, date presets, from/to
                  range) kept, but built entirely with the current HTML's
                  own classes (v-input / v-card / row-col / history-tab-mobile)
                  instead of the old project's searchpage/check-group markup */}
              {show && (
                <div className="history-card v-card v-sheet theme--light elevation-0 px-4 pb-4">
                  <div className="row no-gutters">
                    <div className="col col-12 pt-4 pb-2">
                      <label
                        className="v-label theme--light"
                        style={{ display: "block", marginBottom: 6 }}
                      >
                        {t("GameType")}
                      </label>
                      {Object.keys(gameCategories).map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() =>
                            setFilter((prev) => ({ ...prev, gameType: name }))
                          }
                          className={`history-tab-mobile v-btn v-btn--has-bg theme--light v-size--default mr-2 mb-2 ${
                            filter?.gameType === name ? "primary" : ""
                          }`}
                        >
                          <span className="v-btn__content">
                            <span>{t(name)}</span>
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="col col-12 pb-2">
                      <label
                        className="v-label theme--light"
                        style={{ display: "block", marginBottom: 6 }}
                      >
                        {t("Date")}
                      </label>
                      {timeRanges.map((res) => {
                        const days = res.getDays();
                        const isActive =
                          filter?.fromPeriod === days.fromPeriod &&
                          filter?.toPeriod === days.toPeriod;
                        return (
                          <button
                            key={res.name}
                            type="button"
                            onClick={() =>
                              setFilter((prev) => ({
                                ...prev,
                                fromPeriod: days.fromPeriod,
                                toPeriod: days.toPeriod,
                              }))
                            }
                            className={`history-tab-mobile v-btn v-btn--has-bg theme--light v-size--default mr-2 mb-2 ${
                              isActive ? "primary" : ""
                            }`}
                          >
                            <span className="v-btn__content">
                              <span>{t(res.labelKey)}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="col-6 pr-1">
                      <div className="v-input date-search-mobile mb-3 elevation-0 v-input--hide-details theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined">
                        <div className="v-input__control">
                          <div className="v-input__slot">
                            <fieldset aria-hidden="true">
                              <legend style={{ width: "0px" }}>
                                <span className="notranslate">​</span>
                              </legend>
                            </fieldset>
                            <div className="v-text-field__slot">
                              <input
                                type="date"
                                max={filter?.toPeriod || undefined}
                                value={filter?.fromPeriod}
                                onChange={(e) =>
                                  setFilter((prev) => ({
                                    ...prev,
                                    fromPeriod: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 pl-1">
                      <div className="v-input date-search-mobile mb-3 elevation-0 v-input--hide-details theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined">
                        <div className="v-input__control">
                          <div className="v-input__slot">
                            <fieldset aria-hidden="true">
                              <legend style={{ width: "0px" }}>
                                <span className="notranslate">​</span>
                              </legend>
                            </fieldset>
                            <div className="v-text-field__slot">
                              <input
                                type="date"
                                min={filter?.fromPeriod || undefined}
                                max={currentDate}
                                value={filter?.toPeriod}
                                onChange={(e) =>
                                  setFilter((prev) => ({
                                    ...prev,
                                    toPeriod: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col col-12">
                      <button
                        type="button"
                        onClick={() => setShow(false)}
                        className="history-tab-mobile v-btn v-btn--has-bg theme--light v-size--default primary"
                        style={{ width: "100%" }}
                      >
                        <span className="v-btn__content">
                          <span>{t("Confirm")}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isEmpty(single) ? (
                data?.length > 0 ? (
                  data.map((res) => {
                    const rowTotal = toFixedWithoutRounding(
                      Math.abs(res?.total),
                    );
                    const totalBet = TotalLength(res?.markets);

                    return (
                 
                        <div
                          key={res?.date || res?._id}
                          onClick={() => setSingle(res)}
                          className="row mx-1 mobile-reward-table"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="mr-0 col col-6">
                            <div className="row reward-table-header pl-6 no-gutters">
                              <div className="col col-12">{t("Date")}</div>
                              <div className="col col-12">{t("Platform")}</div>
                              <div className="col col-12">
                                {settledFilter?.betType == "casino"
                                  ? t("Provider")
                                  : t("TotalBets")}
                              </div>
                              <div className="col col-12">{t("Stake")}</div>
                              <div className="col col-12">{t("ProfitLoss")}</div>
                            </div>
                          </div>
                          <div className="col col-6">
                            <div className="row no-gutters">
                              <div className="col col-12">
                                {moment(res?.date).format("DD-MM-YYYY")}
                              </div>
                              <div className="col col-12">
                                {settledFilter?.betType == "casino"
                                  ? t("Casino")
                                  : t("Exchange")}
                              </div>
                              <div className="col col-12">
                                {settledFilter?.betType == "casino"
                                  ? res?.platform
                                  : `${totalBet} Bets`}
                              </div>
                              <div className="col col-12">
                                {res?.totalBetAmount || res?.betAmount}
                              </div>
                              <div
                                className="col col-12"
                                style={{
                                  color: (
                                    settledFilter?.betType == "casino"
                                      ? res?.playerPL > 0
                                      : res?.total > 0
                                  )
                                    ? "green"
                                    : "red",
                                }}
                              >
                                {settledFilter?.betType == "casino"
                                  ? toFixedWithoutRounding(
                                      Math.abs(res?.playerPL),
                                    )
                                  : res?.total > 0
                                    ? toFixedWithoutRounding(Math.abs(rowTotal))
                                    : `(${toFixedWithoutRounding(Math.abs(rowTotal))})`}
                              </div>
                            </div>
                          </div>
                          <div className="col col-12">
                            <hr
                              role="separator"
                              aria-orientation="horizontal"
                              className="mx-2 v-divider theme--light"
                            />
                          </div>
                        </div>
                    
                    );
                  })
                ) : (
                  <div className="row">
                    <div className="text-center pt-0 pb-8 col col-12">
                      <label className="referral-table-header subtitle-2">
                        {t("NoDataAvailable")}
                      </label>
                    </div>
                  </div>
                )
              ) : (
                <div className="row no-gutters">
                  <div className="col col-12 px-4 pt-4 pb-2 d-flex align-center justify-space-between">
                    <span style={{ fontWeight: 600 }}>
                      {t("BettingRecordsDetails")}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSingle({});
                        setActive([]);
                      }}
                      className="history-tab-mobile v-btn v-btn--has-bg theme--light v-size--default"
                    >
                      <span className="v-btn__content">
                        <span>{t("Back")}</span>
                      </span>
                    </button>
                  </div>

                  <div className="col col-12 px-4 pb-2">
                    <div className="row mx-1 mobile-reward-table">
                      <div className="mr-0 col col-6">
                        <div className="row reward-table-header pl-6 no-gutters">
                          <div className="col col-12">{t("ProfitLoss")}</div>
                        </div>
                      </div>
                      <div className="col col-6">
                        <div className="row no-gutters">
                          <div
                            className="col col-12"
                            style={{
                              color: (
                                settledFilter?.betType == "casino"
                                  ? detailList?.playerPL > 0
                                  : single?.total > 0
                              )
                                ? "green"
                                : "red",
                            }}
                          >
                            {settledFilter?.betType == "casino"
                              ? toFixedWithoutRounding(
                                  Math.abs(detailList?.playerPL),
                                )
                              : single?.total > 0
                                ? toFixedWithoutRounding(Math.abs(total))
                                : `(${toFixedWithoutRounding(Math.abs(total))})`}
                          </div>
                        </div>
                      </div>
                      <div  className="col col-12">
                        <hr
                          role="separator"
                          aria-orientation="horizontal"
                          className="mx-2 v-divider theme--light"
                        />
                      </div>
                    </div>
                  </div>

                  {settledFilter?.betType == "casino" ? (
                    <div className="col col-12 px-4">
                      <div className="row mx-1 mobile-reward-table">
                        <div className="mr-0 col col-6">
                          <div className="row reward-table-header pl-6 no-gutters">
                            <div className="col col-12">{t("Casino")}</div>
                            <div className="col col-12">{t("Platform")}</div>
                            <div className="col col-12">{t("GameCode")}</div>
                            <div className="col col-12">{t("BetId")}</div>
                            <div className="col col-12">{t("SettledDate")}</div>
                          </div>
                        </div>
                        <div className="col col-6">
                          <div className="row no-gutters">
                            <div className="col col-12">
                              {detailList?.casinoName}
                            </div>
                            <div className="col col-12">
                              {detailList?.platform}
                            </div>
                            <div className="col col-12">
                              {detailList?.gameCode}
                            </div>
                            <div className="col col-12">
                              {detailList?.casinoBetId}
                            </div>
                            <div className="col col-12">
                              {moment(detailList?.updatedAt).format(
                                "YYYY-MM-DD HH:mm:ss",
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col col-12">
                          <hr
                            role="separator"
                            aria-orientation="horizontal"
                            className="mx-2 v-divider theme--light"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    detailList?.map((res) => (
                      <div key={res?._id} className="col col-12 px-4">
                        <div
                          onClick={() => {
                            setActive((prev) =>
                              prev.includes(res?._id)
                                ? prev.filter((id) => id != res?._id)
                                : [...prev, res?._id],
                            );
                          }}
                          className="row mx-1 mobile-reward-table"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="mr-0 col col-6">
                            <div className="row reward-table-header pl-6 no-gutters">
                              <div className="col col-12">{t("Match")}</div>
                              <div className="col col-12">{t("Market")}</div>
                              <div className="col col-12">{t("AvgOdds")}</div>
                              <div className="col col-12">{t("Stake")}</div>
                              <div className="col col-12">{t("ProfitLoss")}</div>
                              <div className="col col-12">{t("SettledDate")}</div>
                            </div>
                          </div>
                          <div className="col col-6">
                            <div className="row no-gutters">
                              <div className="col col-12">{res?.matchName}</div>
                              <div className="col col-12">
                                {res?.market ||
                                  startCase(settledFilter?.betType)}
                              </div>
                              <div className="col col-12">
                                {settledFilter?.betType != "fancy"
                                  ? res?.bhav
                                  : `${res?.betRun} / ${res?.bhav}`}
                              </div>
                              <div className="col col-12">{res?.amount}</div>
                              <div
                                className="col col-12"
                                style={{
                                  color:
                                    res?.updatedResult > 0 ? "green" : "red",
                                }}
                              >
                                {res?.updatedResult > 0
                                  ? res?.updatedResult
                                  : `(${Math.abs(res?.updatedResult)})`}
                              </div>
                              <div className="col col-12">
                                {moment(res?.updatedAt).format(
                                  "YYYY-MM-DD HH:mm:ss",
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col col-12">
                            <hr
                              role="separator"
                              aria-orientation="horizontal"
                              className="mx-2 v-divider theme--light"
                            />
                          </div>
                        </div>

                        {active.includes(res?._id) && (
                          <div className="row mx-1 mobile-reward-table">
                            <div className="mr-0 col col-6">
                              <div className="row reward-table-header pl-6 no-gutters">
                                <div className="col col-12">{t("Selection")}</div>
                                <div className="col col-12">{t("EventType")}</div>
                                <div className="col col-12">{t("OddsReq")}</div>
                                <div className="col col-12">{t("OddsMatched")}</div>
                                <div className="col col-12">{t("Bid")}</div>
                                <div className="col col-12">{t("BetPlaced")}</div>
                              </div>
                            </div>
                            <div className="col col-6">
                              <div className="row no-gutters">
                                <div className="col col-12">
                                  {startCase(res?.betType || res?.type)} -{" "}
                                  {res?.teamName || res?.fancyName}
                                </div>
                                <div className="col col-12">
                                  {betCheckObj[res?.eventType]}
                                </div>
                                <div className="col col-12">
                                  {settledFilter?.betType != "fancy"
                                    ? res?.bhav
                                    : `${res?.betRun} / ${res?.bhav}`}
                                </div>
                                <div className="col col-12">
                                  {settledFilter?.betType != "fancy"
                                    ? res?.bhav
                                    : `${res?.betRun} / ${res?.bhav}`}
                                </div>
                                <div className="col col-12">
                                  {res?.matchBetId}
                                </div>
                                <div className="col col-12">
                                  {moment(res?.timeInserted).format(
                                    "YYYY-MM-DD HH:mm:ss",
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col col-12">
                              <hr
                                role="separator"
                                aria-orientation="horizontal"
                                className="mx-2 v-divider theme--light"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BettingHistory;
